import React from 'react';
import {
  Pagination as KitPagination,
  PaginationList,
  PaginationItem,
  PaginationButton,
  PaginationPrevious,
  PaginationNext,
  PaginationFirst,
  PaginationLast,
} from '@/design-system/pagination';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/design-system/select';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Replaces the "Showing x – y of n" text (e.g. an Indonesian range). */
  summary?: React.ReactNode;
  /** Label before the page-size select (default "Show:"). */
  pageSizeLabel?: string;
  /** Label after the page-size select (default "/ page"). */
  pageSizeSuffix?: string;
  className?: string;
  'data-testid'?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 40, 50],
  summary,
  pageSizeLabel = 'Show:',
  pageSizeSuffix = '/ page',
  className = 'mt-4',
  'data-testid': testId,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalItems === 0) {
    return null;
  }

  // Max 5 visible pages, windowed around the current page.
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const pages: number[] = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <KitPagination className={className} data-testid={testId}>
      {/* Total items — left */}
      <div className="text-sm tabular-nums text-text-sub-600">
        {summary ?? (
          <>
            Showing{' '}
            <span className="font-medium text-text-strong-950">
              {(currentPage - 1) * itemsPerPage + 1}
            </span>
            {' – '}
            <span className="font-medium text-text-strong-950">
              {Math.min(currentPage * itemsPerPage, totalItems)}
            </span>{' '}
            of{' '}
            <span className="font-medium text-text-strong-950">{totalItems}</span>
          </>
        )}
      </div>

      {/* Page navigation — center */}
      <PaginationList>
        <PaginationItem>
          <PaginationFirst
            disabled={currentPage === 1}
            onClick={() => {
              if (currentPage > 1) {
                onPageChange(1);
              }
            }}
          />
        </PaginationItem>
        <PaginationItem>
          <PaginationPrevious
            disabled={currentPage === 1}
            onClick={() => {
              if (currentPage > 1) {
                onPageChange(currentPage - 1);
              }
            }}
          />
        </PaginationItem>

        {pages.map((page) => (
          <PaginationItem key={page}>
            <PaginationButton
              isActive={currentPage === page}
              onClick={() => onPageChange(page)}
            >
              {page}
            </PaginationButton>
          </PaginationItem>
        ))}

        <PaginationItem>
          <PaginationNext
            disabled={currentPage === totalPages}
            onClick={() => {
              if (currentPage < totalPages) {
                onPageChange(currentPage + 1);
              }
            }}
          />
        </PaginationItem>
        <PaginationItem>
          <PaginationLast
            disabled={currentPage === totalPages}
            onClick={() => {
              if (currentPage < totalPages) {
                onPageChange(totalPages);
              }
            }}
          />
        </PaginationItem>
      </PaginationList>

      {/* Page size selector — right */}
      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <>
            <span className="text-sm text-text-sub-600">{pageSizeLabel}</span>
            <Select
              value={String(itemsPerPage)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
            >
              <SelectTrigger className="h-8 w-fit tabular-nums">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-text-sub-600">{pageSizeSuffix}</span>
          </>
        )}
      </div>
    </KitPagination>
  );
};

export default Pagination;
